import React from 'react';
import { createTheme } from '@mui/material/styles';
import { isMobile } from 'helpers';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { transparentize } from 'polished';
import { etEE, enUS, ruRU, fiFI, svSE, ukUA, plPL, hiIN, deDE } from '@mui/material/locale';
import merge from 'lodash/merge';
import { GROUP_EMPTY_CELL_CLASS } from 'components/DataGrid/cellClasses';
import { buildBrand } from 'brands';

const defaultColor = buildBrand.color;
const defaultDark = false;

export const PICKER_DESKTOP_BREAKPOINT = 600;

const defaultButtonColor = {
  main: '#eee',
  dark: '#ddd',
  contrastText: '#333',
};

const ButtonConf = {
  defaultProps: {
    variant: 'contained',
    color: 'primary',
    disableElevation: true,
  },
  styleOverrides: {
    root: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  // Explicit color="default" button styling. palette.default no longer has
  // `main`, so MUI v6 won't auto-generate these from the palette.
  variants: [
    { props: { color: 'default', variant: 'contained' }, style: { backgroundColor: defaultButtonColor.main, color: defaultButtonColor.contrastText, '&:hover': { backgroundColor: defaultButtonColor.dark } } },
    { props: { color: 'default', variant: 'outlined' }, style: { color: defaultButtonColor.contrastText, borderColor: defaultButtonColor.main, '&:hover': { backgroundColor: defaultButtonColor.main } } },
    { props: { color: 'default', variant: 'text' }, style: { color: defaultButtonColor.contrastText, '&:hover': { backgroundColor: defaultButtonColor.main } } },
  ],
};

const generateCustomColors = (theme) => ({
  guava: theme.palette.augmentColor({
    color: {
      main: defaultColor,
      contrastText: '#fff',
    },
    name: 'guava',
  }),
  statusGreen: theme.palette.augmentColor({
    color: {
      main: '#4daf4f',
      contrastText: '#fff',
    },
    name: 'statusGreen',
  }),
  statusRed: theme.palette.augmentColor({
    color: {
      main: '#f43756',
      contrastText: '#fff',
    },
    name: 'statusRed',
  }),
  statusYellow: theme.palette.augmentColor({
    color: {
      main: '#f1c40f',
      contrastText: '#fff',
    },
    name: 'statusYellow',
  }),
  statusBlue: theme.palette.augmentColor({
    color: {
      main: '#718bc5',
      contrastText: '#fff',
    },
    name: 'statusBlue',
  }),
  statusGray: theme.palette.augmentColor({
    color: {
      main: '#666666',
      contrastText: '#fff',
    },
    name: 'statusGray',
  }),
});

const generateTheme = ({ color, isDark, locale }) => {
  let theme = createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        main: color,
      },
      secondary: {
        main: color,
      },
      // NOTE: palette.default intentionally omits `main` to prevent MUI v6's
      // variant system from generating color="default" variants that override
      // icon/text colors with #eee. Button color="default" is styled via
      // component variants below instead.
      default: {
        contrastText: isDark ? '#eee' : '#333',
      },
      text: {
        primary: isDark ? '#eee' : '#333',
        secondary: '#666',
      },
      // Subtle border for cards, tables and paper outlines. Deliberately not
      // palette.divider: Menu dividers and DataGrid separators derive from
      // divider and become invisible at #eee.
      border: '#eee',
    },
    typography: {
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
      fontSize: isMobile ? 12 : 14,
      htmlFontSize: isMobile ? 14 : 15,
      body1: { fontSize: '1rem' },
      button: { fontSize: '0.9rem' },
    },
    components: {
      MuiAutocomplete: {
        styleOverrides: {
          groupLabel: {
            fontSize: '12px',
            color: '#888',
            lineHeight: 1.3,
            'li:not(:first-of-type) > &': {
              marginTop: '1rem',
            },
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          colorDefault: {
            color: '#fff', // Should be contrastText
            backgroundColor: color,
          },
        },
      },
      MuiDataGrid: {
        defaultProps: {
          disableRowSelectionOnClick: true,

          // Must be turned on manually if needed
          disableRowGrouping: true,
          disableAggregation: true,
          disablePivoting: true,

          checkboxSelection: false,
        },

        styleOverrides: {
          root: ({ theme: t }) => ({
            border: 'none',
            '--DataGrid-rowBorderColor': t.palette.border,
            [`& .${GROUP_EMPTY_CELL_CLASS} .MuiDataGrid-groupingCriteriaCell`]: { color: '#888' },
            [`& .${GROUP_EMPTY_CELL_CLASS} .MuiDataGrid-groupingCriteriaCell > *`]: { color: t.palette.primary.main },
          }),
          toolbar: {
            borderBottomColor: 'var(--DataGrid-rowBorderColor)',
          },
          cell: ({ theme: t }) => ({
            '&:focus, &:focus-within': { outline: 'none', boxShadow: 'none' },
            '&:focus-visible': { outline: `1px solid ${t.palette.primary.main}` },
          }),
          columnHeader: ({ theme: t }) => ({
            '&:focus, &:focus-within': { outline: 'none', boxShadow: 'none' },
            '&:focus-visible': { outline: `1px solid ${t.palette.primary.main}` },
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            padding: '40px 40px 20px 40px', // Actually wanted visual paddings
            ...(isMobile && {
              padding: '20px 20px 10px 20px',
              marginTop: 'calc(32px + var(--safe-area-inset-top))',
              maxHeight: 'calc(100% - 64px - var(--safe-area-inset-top))',
            }),
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          divider: {
            borderBottomColor: '#f5f5f5',
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 32,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '& > .MuiListItemIcon-root > .MuiSvgIcon-root': {
              fontSize: '1.25rem',
            },
            '& .MuiListItemText-secondary': {
              fontSize: '0.8rem',
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paperAnchorBottom: {
            maxHeight: 'calc(100% - var(--safe-area-inset-top))',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '20px 40px', // Actually wanted visual paddings
            margin: '0 -40px', // Escape MuiDialog from the edges

            ...(isMobile && { padding: '10px 20px' }),
            ...(isMobile && { margin: '0 -20px' }),
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          outlined: ({ theme: t }) => ({
            borderColor: t.palette.border,
          }),
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '20px 40px', // Actually wanted visual paddings
            margin: '0 -40px -20px -40px', // Escape MuiDialog from the edges

            ...(isMobile && { padding: '10px 20px' }),
            ...(isMobile && { margin: '0 -20px -10px -20px' }),
          },
        },
      },
      MuiDialogTitle: {
        defaultProps: {
          component: 'div',
          as: 'div',
        },
        styleOverrides: {
          root: {
            fontWeight: 600,
            fontSize: '1.125rem',
            padding: '40px 40px 20px 40px', // Actually wanted paddings
            margin: '-40px -40px 0 -40px', // Escape MuiDialog from the edges

            ...(isMobile && { padding: '20px 20px 10px 20px' }),
            ...(isMobile && { margin: '-20px -20px 0 -20px' }),
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          underline: {
            '&&:before': {
              borderColor: '#ddd',
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: 13,
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          icon: {
            '&&&&&': {
              color: 'inherit',
            },
          },
        },
      },
      MuiTabScrollButton: {
        styleOverrides: {
          root: {
            '&$disabled': {
              opacity: 0.2,
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            'table:not(.no-bg) &:nth-of-type(even)': {
              backgroundColor: '#F9F9F9',
            },
            '&:last-child td': {
              borderBottom: 'none',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            borderBottom: `1px solid ${t.palette.border}`,
          }),
          head: {
            fontWeight: 600,
          },
          stickyHeader: {
            backgroundColor: '#fff',
            '&.MuiTableCell-paddingCheckbox:first-of-type': {
              left: 'initial',
            },
          },
        },
      },
      MuiButtonGroup: {
        defaultProps: {
          disableElevation: true,
        },
      },
      MuiButton: ButtonConf,
      MuiInputLabel: {
        defaultProps: {
          shrink: true,
        },
        styleOverrides: {
          root: {
            '&.MuiInputLabel-shrink': {
              maxWidth: 'calc(100% / 0.85 )',
            },
            '.disable-input-absolute-shrink &.MuiInputLabel-shrink': {
              position: 'relative',
              transform: 'none',
              fontSize: 12,
              maxWidth: '100%',
              whiteSpace: 'wrap',
              marginBottom: '-1rem',
            },
            '.disable-input-absolute-shrink.disable-input-absolute-shrink--big-font &.MuiInputLabel-shrink': {
              fontSize: '1rem',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderColor: '#ddd',
          },
        },
      },
      MuiFormControl: {
        defaultProps: {
          fullWidth: true,
          margin: 'none',
          variant: 'standard',
        },
      },
      MuiUseMediaQuery: {
        noSsr: true,
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            // Allows using ListItemIcon + ListItemText combo in MenuItems.
            // Fixes the alignment after selection and on initial renders.
            // Uses high specificity selectors to not cause problems elsewhere.
            '&:has(.MuiListItemIcon-root + .MuiListItemText-root)': {
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              alignItems: 'center',

              '& > .MuiListItemIcon-root, & > .MuiListItemText-root': {
                display: 'inline-flex',
                alignItems: 'center',
                marginTop: 0,
                marginBottom: 0,

                // Think the Select does a few rerenders. Unsure why... but vertical-align middle
                // seems to further reduce flickering until the parent's flex + alignItems applies.
                verticalAlign: 'middle',
              },
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          fullWidth: true,
          variant: 'standard',
          InputLabelProps: {
            shrink: true,
          },
        },
      },
      MuiRadio: {
        defaultProps: {
          color: 'primary',
          disableRipple: !!isMobile, // Ripple has problems on some devices
          checkedIcon: <CheckCircleIcon />,
        },
      },
      MuiCheckbox: {
        defaultProps: {
          color: 'primary',
          disableRipple: !!isMobile, // Ripple has problems on some devices
        },
      },
      MuiDatePicker: {
        defaultProps: {
          desktopModeMediaQuery: `@media (min-width: ${PICKER_DESKTOP_BREAKPOINT}px)`,
        },
      },
      MuiDateTimePicker: {
        defaultProps: {
          desktopModeMediaQuery: `@media (min-width: ${PICKER_DESKTOP_BREAKPOINT}px)`,
        },
      },
      MuiPickersLayout: {
        styleOverrides: {
          root: {
            maxWidth: 'none',
            [`@media (max-width: ${PICKER_DESKTOP_BREAKPOINT - 0.05}px)`]: {
              '& .MuiPickersLayout-shortcuts': {
                display: 'none',
              },
            },
            '& .MuiChip-root': {
              minHeight: 32,
              height: 'auto',
              maxWidth: 150,
              paddingTop: '4px',
              paddingBottom: '4px',
            },
            '& .MuiChip-label': {
              whiteSpace: 'normal',
            },
          },
        },
      },
    },
    //
    // CUSTOM
    //
    transparentColor: amount => transparentize(amount, color),
    maxWidth: isMobile ? '650px' : '1400px',

    // Main content / feed width
    maxContentWidth: {
      default: '650px',
      medium: '800px',
      wide: '1100px',
    },

    // Where sidebar appears / disappears
    sidebarBreakpoint: '600px',

    feedImageSrcSetSizes: isMobile
      ? '(max-width: 650px) 100vw, 650px' // 100vw until maxContentWidth, then capped at maxContentWidth
      : '(max-width: 600px) 100vw, 650px', // 100vw until sidebarBreakpoint, then capped at maxContentWidth
  });

  theme.zIndex.fullScreenHeader = theme.zIndex.appBar - 7;
  theme.zIndex.navbar = theme.zIndex.appBar - 6;

  // Default tabbar drawers (contact directory, chat etc...)
  theme.zIndex.tabbarBackdrop = theme.zIndex.appBar - 5;
  theme.zIndex.tabbarDrawer = theme.zIndex.appBar - 4;

  // Hub drawer always on top of other tabbar drawers
  theme.zIndex.hubBackdrop = theme.zIndex.appBar - 3;
  theme.zIndex.hubDrawer = theme.zIndex.appBar - 2;

  // Tabbar always on top of the drawers
  theme.zIndex.tabbar = theme.zIndex.appBar - 1;

  // Currently not easy to hide tabbar - put chat drawer on top
  theme.zIndex.chatBackdrop = theme.zIndex.tabbar + 1;
  theme.zIndex.chatDrawer = theme.zIndex.tabbar + 2;

  theme.zIndex.overflowMenu = theme.zIndex.modal + 1;

  theme.zIndex.chatPopups = theme.zIndex.drawer - 2;
  theme.zIndex.helpFloat = theme.zIndex.drawer - 1;

  // Add custom colors to the palette
  theme = createTheme(theme, {
    palette: {
      ...theme.palette,
      ...generateCustomColors(theme),
    },
  });

  return merge({}, locale, theme);
};

const localeMap = {
  et: etEE,
  en: enUS,
  ru: ruRU,
  fi: fiFI,
  // 'lv': ,
  // 'lt': ,
  sv: svSE,
  uk: ukUA,
  pl: plPL,
  hi: hiIN,
  de: deDE,
};

export default ({ color, isDark, language }) => {
  const themeColor = color || defaultColor;
  const themeDark = isDark || defaultDark;

  const locale = localeMap[language] || enUS;

  try {
    return generateTheme({ color: themeColor, isDark: themeDark, locale });
  } catch (e) {
    console.error(e);
    // Something wrong with the color. Use default instead
    return generateTheme(defaultColor, defaultDark, locale);
  }
};
